import { Injectable, Injector, inject } from '@angular/core';
import { Subscription, filter, fromEvent } from 'rxjs';
import { AuthService } from 'src/app/auth/services/auth.service';
import { PhotoDraftService } from '../services/photoDraftService/photo-draft.service';
import { OfflineAvailabilityService } from './offline-availability.service';
import { OFFLINE_STORAGE } from './offline-storage';
import { OfflineStore } from './offline-store.service';
import { IdMapEntry, OfflineSettings, SnapshotRecord } from './offline.models';

/** Gate E-2 b: data of another user who has not signed in on this device for 30 days leaves it */
export const DEVICE_DATA_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;
/** a visible app counts as use of the device, written at most once per day (Rev1 review H4) */
export const DEVICE_ACTIVITY_WRITE_MS = 24 * 60 * 60 * 1000;
/** records of the device itself in offlineSettings (e.g. device:legacyOwner, FE-G9) are no users */
const DEVICE_RECORD = /^device:/;

/**
 * Offline data on shared devices (Security-Gate Offline E2, FE-G2, user decision E-2 a+b): at logout the user may
 * remove his offline data from this device, and the data of other users expires after 30 days without sign-in.
 * Waiting work is never removed: the outbox with its files and id map, and photo drafts stay (spec §6).
 * Reads across users on purpose (expiry); every other offline read stays scoped in OfflineStore.
 */
@Injectable({ providedIn: 'root' })
export class OfflineDeviceDataService {
  private readonly storage = inject(OFFLINE_STORAGE);
  private readonly store = inject(OfflineStore);
  private readonly auth = inject(AuthService);
  private readonly photoDrafts = inject(PhotoDraftService);
  // lazy: OfflineAvailabilityService itself needs the store and the engine (no cycle at construction)
  private readonly injector = inject(Injector);

  /** test seam: clock in ms */
  now: () => number = () => Date.now();

  /** «Offline-Daten dieses Geräts entfernen» at logout: only with data on the device and nothing waiting (E-2 a) */
  async canRemoveOwnData(): Promise<boolean> {
    const userId = this.auth.getUserId();
    if (!userId || !(await this.nothingWaiting(userId))) return false;
    const snapshots = await this.storage.listByUser<SnapshotRecord>('snapshots', userId);
    return snapshots.length > 0;
  }

  /** removes snapshots, saved plans, id map and settings of the signed-in user; false when something waits (checked again) */
  async removeOwnData(): Promise<boolean> {
    const userId = this.auth.getUserId();
    if (!userId || !(await this.nothingWaiting(userId))) return false;
    // a snapshot refresh or a «Für offline speichern» still running must not write back after the removal (Rev1 H5)
    await this.injector.get(OfflineAvailabilityService).freezeUser(userId);
    await this.removeUser(userId, false);
    return true;
  }

  /**
   * A tablet where the app stays open for weeks is in use: becoming visible refreshes «last sign-in» of the signed-in
   * user, at most once a day, so another user's sign-in never expires his data (Rev1 review H4). Never throws.
   */
  async touch(): Promise<void> {
    const userId = this.auth.getUserId();
    if (!userId) return;
    try {
      const now = this.now();
      const settings = await this.storage.get<OfflineSettings>('offlineSettings', userId);
      if (typeof settings?.lastSeenAt === 'number' && now - settings.lastSeenAt < DEVICE_ACTIVITY_WRITE_MS) return;
      await this.store.updateSettings(userId, (current) => ({ ...current, lastSeenAt: now }));
    } catch (error) {
      console.warn('[OfflineDeviceData] activity not stored', error instanceof Error ? error.name : 'unknown');
    }
  }

  /** app start: every change to «visible» calls touch() */
  watchActivity(): Subscription {
    return fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'))
      .subscribe(() => void this.touch());
  }

  /**
   * App start with a session and every sign-in: this user is active now, other users not seen for 30 days lose their
   * snapshots, saved plans and settings (E-2 b). A row without a time starts the 30 days now (no removal right after
   * the update). Never throws: the next start tries again.
   */
  async markActiveAndExpire(userId: string): Promise<void> {
    try {
      const now = this.now();
      await this.store.updateSettings(userId, (settings) => ({ ...settings, lastSeenAt: now }));
      const settings = (await this.storage.listAll<OfflineSettings>('offlineSettings')).filter((row) => !DEVICE_RECORD.test(row.userId));
      const lastSeen = new Map(settings.map((row) => [row.userId, row.lastSeenAt]));
      // snapshots of a user without settings row count too (they would otherwise never expire)
      // only the userId index keys are read: snapshot rows carry plan bytes
      for (const snapshotUser of await this.storage.listUserIds('snapshots')) {
        if (!lastSeen.has(snapshotUser)) lastSeen.set(snapshotUser, undefined);
      }
      for (const [other, seenAt] of lastSeen) {
        if (other === userId) continue;
        if (typeof seenAt !== 'number') {
          await this.store.updateSettings(other, (current) => ({ ...current, lastSeenAt: now }));
        } else if (now - seenAt > DEVICE_DATA_EXPIRY_MS) {
          const waiting = (await this.store.listOutbox(other)).length > 0;
          await this.removeUser(other, waiting);
        }
      }
    } catch (error) {
      console.warn('[OfflineDeviceData] expiry failed', error instanceof Error ? error.name : 'unknown');
    }
  }

  private async nothingWaiting(userId: string): Promise<boolean> {
    if ((await this.store.listOutbox(userId)).length > 0) return false;
    const drafts = await this.photoDrafts.listAll();
    return drafts.every((draft) => draft.count === 0);
  }

  /** keepOutboxLinks: the user still has outbox entries; their files and the id map stay for the sync */
  private async removeUser(userId: string, keepOutboxLinks: boolean): Promise<void> {
    await this.storage.deleteWhere('snapshots', 'userId', userId);
    await this.storage.delete('offlineSettings', userId);
    if (keepOutboxLinks) {
      // the mapping stays for the sync, the copies of sent creations (task texts, T13) go (Rev1 review H6)
      for (const row of await this.storage.listByUser<IdMapEntry>('idMap', userId)) {
        if (!row.synced) continue;
        const mapping: IdMapEntry = { localId: row.localId, serverId: row.serverId, type: row.type, userId: row.userId };
        await this.storage.put('idMap', mapping);
      }
      return;
    }
    await this.storage.deleteWhere('idMap', 'userId', userId);
    await this.storage.deleteWhere('blobs', 'userId', userId);
  }
}
